// Centralized authentication service
import api, { storage } from './apiClient';
import { AUTH_REQUEST_TIMEOUT_MS } from '../utils/constants';

/**
 * Dispatches a custom event with error handling
 * @param {string} eventName - Name of the custom event
 */
function dispatchAuthEvent(eventName) {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(eventName));
    }
  } catch (error) {
    console.error(`auth: failed to dispatch ${eventName} event`, error);
  }
}

/**
 * Registers a new user
 * @param {string} username - Username
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} passwordConfirm - Password confirmation
 * @param {number|null} university - University id
 * @param {string} universityOther - Free-text value when university is "Other"
 * @param {string} referralCode - Optional referral code
 * @returns {Promise<Object>} Response data
 */
export async function register(
  username,
  email,
  password,
  passwordConfirm,
  university = null,
  universityOther = '',
  referralCode = ''
) {
  const payload = {
    username,
    email,
    password,
    password_confirm: passwordConfirm,
    university,
    university_other: universityOther
  };
  if (referralCode) {
    payload.referral_code = referralCode;
  }

  const response = await api.post(
    '/api/auth/register/',
    payload,
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: AUTH_REQUEST_TIMEOUT_MS
    }
  );

  return response.data;
}

/**
 * Authenticates user and stores tokens
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} Response data containing access, refresh tokens and user info
 */
export async function login(email, password) {
  const response = await api.post(
    '/api/auth/login/',
    { email, password },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: AUTH_REQUEST_TIMEOUT_MS
    }
  );

  const { access, refresh, user } = response.data;

  // Store tokens and user data using centralized storage adapter
  storage.setItem('access_token', access);
  storage.setItem('refresh_token', refresh);
  storage.setItem('user', JSON.stringify(user));

  // Notify components of login state change
  dispatchAuthEvent('auth:login');

  return response.data;
}

/**
 * Logs out user and clears all auth data
 * @returns {Promise<void>}
 */
export async function logout() {
  const refresh = storage.getItem('refresh_token');

  try {
    if (refresh) {
      // Best-effort server logout (blacklist refresh token)
      await api.post('/api/auth/logout/', { refresh });
    }
  } catch (error) {
    // Ignore network/API errors and proceed with client cleanup
    console.error('auth:logout - server logout failed, proceeding with local cleanup', error);
  } finally {
    // Clear all auth-related data
    storage.removeItem('access_token');
    storage.removeItem('refresh_token');
    storage.removeItem('user');
    storage.removeItem('selected_topic_id');
    storage.removeItem('selected_topic_name');

    // Notify components of logout state change
    dispatchAuthEvent('auth:logout');
  }
}

/**
 * Gets the current user from storage
 * @returns {Object|null} User object or null if not found/invalid
 */
export function getUser() {
  try {
    const raw = storage.getItem('user');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error('auth:getUser - failed to parse user data from storage', error);
    return null;
  }
}

/**
 * Updates the user data in storage
 * @param {Object} user - User object to store
 */
export function setUser(user) {
  try {
    storage.setItem('user', JSON.stringify(user));
  } catch (error) {
    console.error('auth:setUser - failed to store user data', error);
  }
}

/**
 * Checks if user is authenticated
 * @returns {boolean} True if user has access token
 */
export function isAuthenticated() {
  return !!storage.getItem('access_token');
}

/**
 * Updates access and refresh tokens (used after password change)
 * @param {string} access - New access token
 * @param {string} refresh - New refresh token (optional)
 */
export function updateTokens(access, refresh) {
  if (access) {
    storage.setItem('access_token', access);
  }
  if (refresh) {
    storage.setItem('refresh_token', refresh);
  }

  // Notify components of login state change
  dispatchAuthEvent('auth:login');
}
